// @ts-strict-ignore
import { channelUrl } from "@dashboard/channels/urls";
import { useAnalyticsMode } from "@dashboard/components/AnalyticsMode/AnalyticsModeProvider";
import useAppChannel from "@dashboard/components/AppLayout/AppChannelContext";
import { formatMoney } from "@dashboard/components/Money";
import { history } from "@dashboard/components/Router";
import { customerUrl } from "@dashboard/customers/urls";
import { useWizardPersistence } from "@dashboard/onboardingWizard/context/wizardStorage";
import { orderListUrl, orderUrl } from "@dashboard/orders/urls";
import { productListUrl, ProductListUrlSortField } from "@dashboard/products/urls";
import { Box, Button, Text } from "@saleor/macaw-ui-next";
import {
  AlertOctagon,
  AlertTriangle,
  BarChart3,
  Boxes,
  CheckCircle2,
  Clock,
  Package,
  PackageX,
  Receipt,
  Repeat,
  Rocket,
  RotateCcw,
  ShoppingCart,
  Sparkles,
  Timer,
  Truck,
  UserCheck,
  UserMinus,
  UserPlus,
  Users,
  Wallet,
  XCircle,
} from "lucide-react";
import React from "react";
import { useIntl } from "react-intl";

// import { AiChatSection } from "./components/AiChatSection";
import { AnalyticsFilters as AnalyticsFiltersV2 } from "./components/v2/AnalyticsFilters";
import { BarChartTile as BarChartTileV2 } from "./components/v2/BarChartTile";
import { ChannelPerformanceTable } from "./components/v2/ChannelPerformanceTable";
import { ChartTile as ChartTileV2 } from "./components/v2/ChartTile";
import { CohortTable } from "./components/v2/CohortTable";
import { toCsv } from "./components/v2/csv";
import { CustomersTable } from "./components/v2/CustomersTable";
import { DiscountAnomaliesTile } from "./components/v2/DiscountAnomaliesTile";
import { DonutChartTile as DonutChartTileV2 } from "./components/v2/DonutChartTile";
import { ExportButton } from "./components/v2/ExportButton";
import { formatDurationHours } from "./components/v2/format";
import { FunnelTile } from "./components/v2/FunnelTile";
import { InventoryTable } from "./components/v2/InventoryTable";
import { KpiTile as KpiTileV2 } from "./components/v2/KpiTile";
import { OrdersHeatmap } from "./components/v2/OrdersHeatmap";
import { SectionHeader } from "./components/v2/SectionHeader";
import { chartPalette } from "./components/v2/tokens";
import { useAnalyticsData } from "./hooks/useAnalyticsData";
import { useAnalyticsFilters } from "./hooks/useAnalyticsFilters";
import { analyticsMessages } from "./messages";

export const AnalyticsDashboard: React.FC = () => {
  const { availableChannels, channel, setChannel } = useAppChannel(false);
  const { preset, dateRange, setPreset } = useAnalyticsFilters();
  const intl = useIntl();
  const TOP_N = 5;
  const SLA_HOURS = 96;

  const channelId = channel?.id;
  const { data } = useAnalyticsData(channelId, dateRange);
  const { isLive } = useAnalyticsMode();
  const { loading: wizardStateLoading, wizardCompleted } = useWizardPersistence();

  // Sparkline + chart series come from the daily sales trend (current period only).
  const { sparkSales, currentSeries, currentTotal } = React.useMemo(() => {
    const series = data.salesSeries ?? [];

    return {
      sparkSales: series.map(p => p.value || 0),
      currentSeries: series,
      currentTotal: series.reduce((s, p) => s + (p.value || 0), 0),
    };
  }, [data.salesSeries]);

  // Real compare-to-prior deltas from the hook.
  const cmp = data.comparison ?? {
    gmvDelta: null,
    ordersDelta: null,
    aovDelta: null,
    refundRateDelta: null,
    customersDelta: null,
    checkoutsDelta: null,
    priorRange: { from: "", to: "" },
  };

  const currency = channel?.currencyCode ?? "USD";

  const formatCurrencyShort = (v: number) => {
    const abs = Math.abs(v);

    if (abs >= 1_000_000) return `${currency} ${(v / 1_000_000).toFixed(1)}M`;

    if (abs >= 1_000) return `${currency} ${(v / 1_000).toFixed(1)}k`;

    return `${currency} ${v.toFixed(0)}`;
  };
  const presetLabel =
    preset === "last7d"
      ? intl.formatMessage(analyticsMessages.presetLast7d)
      : preset === "last30d"
        ? intl.formatMessage(analyticsMessages.presetLast30d)
        : intl.formatMessage(analyticsMessages.presetLast90d);

  // Removed '(live)' badge from titles per request
  return (
    <Box
      display="grid"
      gap={7}
      gridTemplateColumns={{ mobile: 1, tablet: 2, desktop: 2 }}
      alignItems="start"
      paddingX={8}
      paddingY={6}
      paddingTop={9}
    >
      {/* Store Setup Banner */}
      {!wizardStateLoading && !wizardCompleted && (
        <Box
          __gridColumn="1/-1"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          padding={6}
          borderRadius={4}
          __backgroundColor="#F0F4FF"
          borderColor="default1"
          borderWidth={1}
          borderStyle="solid"
        >
          <Box display="flex" alignItems="center" gap={4}>
            <Box
              __width={44}
              __height={44}
              borderRadius={4}
              __backgroundColor="#E0E8FF"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Rocket size={22} color="#3B5BDB" />
            </Box>
            <Box display="flex" flexDirection="column" gap={1}>
              <Text size={5} fontWeight="bold">
                Complete your store setup
              </Text>
              <Text size={3} color="default2">
                Finish configuring your store to start selling. Set up company info, catalog, payments, and more.
              </Text>
            </Box>
          </Box>
          <Button
            variant="primary"
            onClick={() => history.push("/wizard")}
          >
            Start Onboarding
          </Button>
        </Box>
      )}

      {/* 1) Revenue & Sales Performance */}
      <Box __gridColumn="1/-1">
        <SectionHeader
          icon={<BarChart3 size={18} />}
          title={intl.formatMessage(analyticsMessages.sectionRevenue)}
          description={presetLabel}
          accentColor={chartPalette.primary}
          right={
            <AnalyticsFiltersV2
              preset={preset}
              channels={availableChannels}
              channelId={channelId}
              onChangeChannel={setChannel}
              dateRange={dateRange}
              onChangePreset={setPreset}
            />
          }
        />

        <Box display="grid" gap={5} gridTemplateColumns={{ mobile: 1, tablet: 2, desktop: isLive ? 3 : 4 }}>
          <KpiTileV2
            title={intl.formatMessage(analyticsMessages.totalSalesGMV)}
            value={formatMoney({ amount: data.gmv, currency }, intl.locale)}
            icon={<BarChart3 size={16} />}
            accentColor={chartPalette.primary}
            delta={cmp.gmvDelta}
            spark={sparkSales}
          />
          <KpiTileV2
            title={intl.formatMessage(analyticsMessages.netSales)}
            value={formatMoney({ amount: data.netSales, currency }, intl.locale)}
            icon={<Receipt size={16} />}
            accentColor={chartPalette.success}
            delta={cmp.gmvDelta}
            spark={sparkSales}
          />
          <KpiTileV2
            title={intl.formatMessage(analyticsMessages.avgOrderValue)}
            value={formatMoney({ amount: data.avgOrderValue, currency }, intl.locale)}
            icon={<ShoppingCart size={16} />}
            accentColor={chartPalette.info}
            delta={cmp.aovDelta}
          />
          {!isLive && (
            <KpiTileV2
              title={intl.formatMessage(analyticsMessages.avgItemsPerOrder)}
              value={data.avgItemsPerOrder}
              icon={<Package size={16} />}
              accentColor={chartPalette.warning}
            />
          )}
        </Box>

        <Box marginTop={5}>
          <ChartTileV2
            title={intl.formatMessage(analyticsMessages.grossSalesDailyTrend)}
            data={currentSeries}
            totalValue={formatCurrencyShort(currentTotal)}
            delta={cmp.gmvDelta}
            currency={currency}
            color={chartPalette.primary}
          />
        </Box>

        <Box
          marginTop={5}
          display="grid"
          gap={5}
          gridTemplateColumns={{ mobile: 1, tablet: 2, desktop: 2 }}
        >
          <BarChartTileV2
            title={intl.formatMessage(analyticsMessages.bestSellersTopN, { topN: TOP_N })}
            description="Highest-volume movers in the selected range"
            data={data.bestSellers.slice(0, TOP_N)}
            labelKey="name"
            valueKey="units"
            labelMaxChars={12}
            colorRamp
            color={chartPalette.primary}
          />
          <BarChartTileV2
            title={intl.formatMessage(analyticsMessages.topSkusByRevenue, { topN: TOP_N })}
            description="Highest-revenue contributors — not always the same as best sellers"
            data={[...data.bestSellers].sort((a, b) => b.gmv - a.gmv).slice(0, TOP_N)}
            labelKey="name"
            valueKey="gmv"
            labelMaxChars={12}
            colorRamp
            color={chartPalette.success}
            valueFormatter={formatCurrencyShort}
          />
        </Box>

        <Box
          marginTop={5}
          display="grid"
          gap={5}
          gridTemplateColumns={{ mobile: 1, tablet: 2, desktop: 2 }}
        >
          <DonutChartTileV2
            title={intl.formatMessage(analyticsMessages.salesByChannelCurrency)}
            data={data.salesByChannel.map(c => ({
              label: `${c.channel} / ${c.currency}`,
              value: c.gmv,
            }))}
            labelKey="label"
            valueKey="value"
            valueFormatter={formatCurrencyShort}
            centerLabel="GMV"
          />
          <DonutChartTileV2
            title={intl.formatMessage(analyticsMessages.salesByCategory)}
            data={data.salesByCategory}
            labelKey="category"
            valueKey="gmv"
            valueFormatter={formatCurrencyShort}
            centerLabel="GMV"
          />
        </Box>

        <Box marginTop={5}>
          <ChannelPerformanceTable
            title={intl.formatMessage(analyticsMessages.channelPerformance)}
            description="Per-channel orders, GMV, AOV, refund rate, and share of total"
            rows={data.channelPerformance ?? []}
            columns={{
              channel: intl.formatMessage(analyticsMessages.colChannel),
              orders: intl.formatMessage(analyticsMessages.colOrders),
              gmv: intl.formatMessage(analyticsMessages.colGmv),
              aov: intl.formatMessage(analyticsMessages.colAov),
              refundRate: "Refund %",
              share: intl.formatMessage(analyticsMessages.colShare),
            }}
            formatMoneyFor={(amount, c) => formatMoney({ amount, currency: c || currency }, intl.locale)}
            onRowClick={row => {
              if (row.channelId) history.push(channelUrl(row.channelId));
            }}
            headerActions={
              <ExportButton
                filename={`channel-performance_${dateRange.from}_${dateRange.to}`}
                getCsv={() =>
                  toCsv(
                    ["Channel", "Currency", "Orders", "GMV", "AOV", "Refund %", "Share %"],
                    (data.channelPerformance ?? []).map(r => [
                      r.channel,
                      r.currency,
                      r.orders,
                      r.gmv.toFixed(2),
                      r.aov.toFixed(2),
                      (r.refundRate * 100).toFixed(2),
                      (r.share * 100).toFixed(2),
                    ]),
                  )
                }
              />
            }
          />
        </Box>
      </Box>
      {/* 2) Orders & Checkout */}
      <Box __gridColumn="1/-1">
        <SectionHeader
          icon={<ShoppingCart size={18} />}
          title={intl.formatMessage(analyticsMessages.sectionOrders)}
          description="Order flow, checkout conversion, and discount impact"
          accentColor={chartPalette.info}
        />
        <Box display="grid" gap={5} gridTemplateColumns={{ mobile: 1, tablet: 2, desktop: 3 }}>
          <KpiTileV2
            title={intl.formatMessage(analyticsMessages.totalOrders)}
            value={data.ordersCount}
            subtext={presetLabel}
            icon={<Receipt size={16} />}
            accentColor={chartPalette.info}
            delta={cmp.ordersDelta}
          />
          <KpiTileV2
            title={intl.formatMessage(analyticsMessages.cancellations)}
            value={data.cancellationsCount}
            subtext={`${(data.cancellationsRate * 100).toFixed(1)}% cancellation rate`}
            icon={<XCircle size={16} />}
            accentColor={chartPalette.danger}
            invertDelta
          />
          <KpiTileV2
            title={`Stuck orders (>${data.stuckOrdersThresholdDays}d)`}
            value={data.stuckOrdersCount}
            subtext="Unfulfilled or partial. Click to open the Orders list."
            icon={<AlertOctagon size={16} />}
            accentColor={chartPalette.danger}
            invertDelta
            info={`Orders created more than ${data.stuckOrdersThresholdDays} days ago that still aren't fully fulfilled. A backlog signal.`}
            onClick={() => history.push(orderListUrl())}
          />
        </Box>

        <Box
          marginTop={5}
          display="grid"
          gap={5}
          gridTemplateColumns={{ mobile: 1, tablet: 1, desktop: 2 }}
        >
          <FunnelTile
            title="Conversion Funnel"
            description={`Checkouts started → Orders placed → Fulfilled · vs ${cmp.priorRange.from} – ${cmp.priorRange.to}`}
            stages={data.funnel ?? []}
            stageDeltas={{
              checkouts: cmp.checkoutsDelta,
              orders: cmp.ordersDelta,
            }}
          />
          <DonutChartTileV2
            title={intl.formatMessage(analyticsMessages.ordersByStatus)}
            data={data.statusBreakdown}
            labelKey="status"
            valueKey="count"
            centerLabel="Orders"
            onSliceClick={status => {
              if (!status) return;

              history.push(orderListUrl({ status: [String(status).toLowerCase()] as any }));
            }}
          />
        </Box>

        <Box marginTop={5}>
          <OrdersHeatmap
            title="When orders come in"
            description="Order density by day of week and hour of day (local time). Use it to schedule promos, push notifications, and support staffing."
            data={data.ordersHeatmap ?? []}
            baseColor={chartPalette.info}
          />
        </Box>

        <Box marginTop={5}>
          <DiscountAnomaliesTile
            title="Discount & promo impact"
            description="Daily share of GMV given away as discounts, plus the most-discounted individual orders."
            daily={data.discountByDay ?? []}
            topOrders={data.topDiscountedOrders ?? []}
            formatMoney={amount => formatMoney({ amount, currency }, intl.locale)}
            onOrderClick={o => o.id && history.push(orderUrl(o.id))}
          />
        </Box>
      </Box>

      {/* 3) Customers & Retention */}
      <Box __gridColumn="1/-1">
        <SectionHeader
          icon={<Users size={18} />}
          title={intl.formatMessage(analyticsMessages.sectionCustomers)}
          description="Loyalty, recency, and your highest-value buyers"
          accentColor={chartPalette.purple}
        />

        {/* New vs Returning + retention KPIs */}
        {(() => {
          const segments = data.segments ?? {
            newCustomers: 0,
            returningCustomers: 0,
            newGmv: 0,
            returningGmv: 0,
          };
          const totalCustomers = segments.newCustomers + segments.returningCustomers;
          const totalSegGmv = segments.newGmv + segments.returningGmv;
          const newShare = totalCustomers > 0 ? segments.newCustomers / totalCustomers : 0;
          const returningShare = totalCustomers > 0 ? segments.returningCustomers / totalCustomers : 0;
          const returningGmvShare = totalSegGmv > 0 ? segments.returningGmv / totalSegGmv : 0;

          return (
            <Box display="grid" gap={5} gridTemplateColumns={{ mobile: 1, tablet: 2, desktop: 3 }}>
              <KpiTileV2
                title="New customers"
                value={segments.newCustomers}
                subtext={`${(newShare * 100).toFixed(1)}% of buyers · ${formatMoney({ amount: segments.newGmv, currency }, intl.locale)} GMV`}
                icon={<UserPlus size={16} />}
                accentColor={chartPalette.info}
                delta={cmp.customersDelta}
                info="Buyers whose first-ever order is in this range, plus guest checkouts. Delta compares total active buyers vs prior period."
              />
              <KpiTileV2
                title="Returning customers"
                value={segments.returningCustomers}
                subtext={`${(returningShare * 100).toFixed(1)}% of buyers · ${formatMoney({ amount: segments.returningGmv, currency }, intl.locale)} GMV`}
                icon={<UserCheck size={16} />}
                accentColor={chartPalette.success}
                info="Buyers with more than one lifetime order. Strong proxy for retention health."
              />
              <KpiTileV2
                title="Revenue from returning"
                value={`${(returningGmvShare * 100).toFixed(1)}%`}
                subtext="of GMV in selected range"
                icon={<Sparkles size={16} />}
                accentColor={chartPalette.purple}
                info="Share of GMV driven by returning customers. Healthy ecommerce stores typically see 30–50%+."
              />
              <KpiTileV2
                title={intl.formatMessage(analyticsMessages.repeatPurchaseRate)}
                value={`${(data.repeatRate * 100).toFixed(1)}%`}
                icon={<Repeat size={16} />}
                accentColor={chartPalette.success}
              />
              <KpiTileV2
                title={intl.formatMessage(analyticsMessages.timeToSecondOrder)}
                value={`${Math.round(data.timeToSecondOrderDays || 0)} ${intl.formatMessage(analyticsMessages.unitDays)}`}
                icon={<Clock size={16} />}
                accentColor={chartPalette.info}
                subtext={data.timeToSecondOrderDays > 0 ? `~${data.timeToSecondOrderDays.toFixed(1)} days exact` : undefined}
              />
              {!isLive && (
                <KpiTileV2
                  title={intl.formatMessage(analyticsMessages.churnRisk)}
                  value={`${(data.churnRiskRate * 100).toFixed(1)}%`}
                  icon={<UserMinus size={16} />}
                  accentColor={chartPalette.danger}
                  invertDelta
                />
              )}
            </Box>
          );
        })()}

        <Box marginTop={5}>
          <CohortTable
            title="Cohort retention by signup month"
            description="Buyers grouped by the month they joined. Repeat rate = % with more than one lifetime order."
            rows={data.cohorts ?? []}
            formatMoney={v => formatMoney({ amount: v, currency }, intl.locale)}
            headerActions={
              <ExportButton
                filename={`cohorts_${dateRange.from}_${dateRange.to}`}
                getCsv={() =>
                  toCsv(
                    ["Cohort", "Customers", "Repeat rate %", "Avg orders", "Avg GMV"],
                    (data.cohorts ?? []).map(r => [
                      r.cohort,
                      r.size,
                      (r.repeatRate * 100).toFixed(2),
                      r.avgOrders.toFixed(2),
                      r.avgGmv.toFixed(2),
                    ]),
                  )
                }
              />
            }
          />
        </Box>

        <Box marginTop={5}>
          <CustomersTable
            title={intl.formatMessage(analyticsMessages.topCustomers)}
            description={`Top ${TOP_N} by gross merchandise value`}
            rows={data.topCustomers.slice(0, TOP_N)}
            columns={{
              customer: intl.formatMessage(analyticsMessages.colCustomer),
              orders: intl.formatMessage(analyticsMessages.colOrders),
              gmv: intl.formatMessage(analyticsMessages.colGmv),
            }}
            valueFormatter={v => formatMoney({ amount: v, currency }, intl.locale)}
            onRowClick={r => {
              // We only have customer email/id stored as a single string. Best-effort:
              // open customer search if it looks like an email, otherwise treat as id.
              const looksLikeEmail = r.customer.includes("@");

              history.push(
                looksLikeEmail
                  ? `/customers/?asc=true&sort=name`
                  : customerUrl(r.customer),
              );
            }}
            headerActions={
              <ExportButton
                filename={`top-customers_${dateRange.from}_${dateRange.to}`}
                getCsv={() =>
                  toCsv(
                    ["Customer", "Orders", "GMV"],
                    data.topCustomers.map(r => [r.customer, r.orders, r.gmv.toFixed(2)]),
                  )
                }
              />
            }
          />
        </Box>
      </Box>

      {/* 4) Inventory & Merchandising */}
      <Box __gridColumn="1/-1">
        <SectionHeader
          icon={<Boxes size={18} />}
          title={intl.formatMessage(analyticsMessages.sectionInventory)}
          description="Stock health, replenishment urgency, and dead inventory"
          accentColor={chartPalette.warning}
        />
        <Box display="grid" gap={5} gridTemplateColumns={{ mobile: 1, tablet: 2, desktop: 2 }}>
          <BarChartTileV2
            title={intl.formatMessage(analyticsMessages.lowStockAlertsTopN, { topN: TOP_N })}
            description="Variants with the smallest available stock"
            data={data.lowStock
              .slice()
              .sort((a, b) => a.available - b.available)
              .slice(0, TOP_N)}
            labelKey="variant"
            valueKey="available"
            labelMaxChars={16}
            color={chartPalette.warning}
            referenceLines={[
              { y: 7, color: "#FACC15", strokeDasharray: "4 4", label: "≤7" },
              { y: 5, color: "#F97316", strokeDasharray: "4 4", label: "≤5" },
              { y: 3, color: "#EF4444", strokeDasharray: "4 4", label: "≤3" },
            ]}
          />
          <Box display="grid" gap={5}>
            <KpiTileV2
              title={intl.formatMessage(analyticsMessages.outOfStockSkus)}
              value={data.outOfStockSkus}
              subtext={presetLabel}
              icon={<PackageX size={16} />}
              accentColor={chartPalette.danger}
              invertDelta
              info="Number of tracked SKUs with zero available stock right now."
            />
            <KpiTileV2
              title={intl.formatMessage(analyticsMessages.lowStockThreshold)}
              value={`≤ ${data.lowStockThreshold} ${intl.formatMessage(analyticsMessages.unitUnits)}`}
              subtext={presetLabel}
              icon={<AlertTriangle size={16} />}
              accentColor={chartPalette.warning}
              info="SKUs at or below this on-hand quantity are flagged as low stock."
            />
          </Box>
        </Box>
        <Box
          marginTop={5}
          display="grid"
          gap={5}
          gridTemplateColumns={{ mobile: 1, tablet: 2, desktop: 2 }}
        >
          <InventoryTable
            title="Stockout Risk (DOH)"
            description="SKUs with the lowest days of inventory remaining at the current sales pace."
            info="DOH = on-hand units ÷ daily sales velocity in the selected range. Only SKUs that actually sold are included — never-sold inventory appears in Dead Stock."
            rows={data.doh.slice(0, TOP_N)}
            columns={{
              sku: intl.formatMessage(analyticsMessages.colSku),
              days: intl.formatMessage(analyticsMessages.colDays),
            }}
            severityMode="high-is-good"
            unit={intl.formatMessage(analyticsMessages.unitDays)}
            onRowClick={r => history.push(productListUrl({ asc: true, sort: ProductListUrlSortField.name }) + `&query=${encodeURIComponent(r.sku)}`)}
            headerActions={
              <ExportButton
                filename={`doh_${dateRange.from}_${dateRange.to}`}
                getCsv={() => toCsv(["SKU", "Days"], data.doh.map(r => [r.sku, r.days]))}
              />
            }
          />
          <InventoryTable
            title={intl.formatMessage(analyticsMessages.deadStock)}
            description="SKUs with stock available but zero sales in the selected range — capital tied up."
            info="Dead stock has on-hand inventory but recorded no sales during the selected period. The value shown is the on-hand quantity."
            rows={data.deadStock.slice(0, TOP_N)}
            columns={{
              sku: intl.formatMessage(analyticsMessages.colSku),
              days: intl.formatMessage(analyticsMessages.unitUnits),
            }}
            severityMode="high-is-bad"
            unit={intl.formatMessage(analyticsMessages.unitUnits)}
            onRowClick={r => history.push(productListUrl({ asc: true, sort: ProductListUrlSortField.name }) + `&query=${encodeURIComponent(r.sku)}`)}
            headerActions={
              <ExportButton
                filename={`dead-stock_${dateRange.from}_${dateRange.to}`}
                getCsv={() => toCsv(["SKU", "Units"], data.deadStock.map(r => [r.sku, r.days]))}
              />
            }
          />
        </Box>
      </Box>

      {/* 5) Fulfillment & Ops */}
      <Box __gridColumn="1/-1">
        <SectionHeader
          icon={<Truck size={18} />}
          title={intl.formatMessage(analyticsMessages.sectionFulfillment)}
          description="Pick, pack, ship — and the money that comes back"
          accentColor={chartPalette.teal}
        />
        <Box display="grid" gap={5} gridTemplateColumns={{ mobile: 1, tablet: 2, desktop: isLive ? 3 : 4 }}>
          <KpiTileV2
            title={intl.formatMessage(analyticsMessages.avgTimeToFulfill)}
            value={formatDurationHours(data.avgFulfillmentHours)}
            icon={<Timer size={16} />}
            accentColor={chartPalette.info}
            info="Average elapsed time between order creation and the first fulfillment event, across fully fulfilled orders in range."
          />
          <KpiTileV2
            title={intl.formatMessage(analyticsMessages.onTimeFulfillmentRateWithSla, {
              slaHours: SLA_HOURS,
            })}
            value={`${(data.onTimeFulfillmentRate * 100).toFixed(1)}%`}
            icon={<CheckCircle2 size={16} />}
            accentColor={chartPalette.success}
            info={`Share of fulfilled orders shipped within ${SLA_HOURS}h of being created.`}
          />
          <KpiTileV2
            title={intl.formatMessage(analyticsMessages.refundReturnRate)}
            value={`${(data.refundRate * 100).toFixed(1)}%`}
            icon={<RotateCcw size={16} />}
            accentColor={chartPalette.danger}
            invertDelta
            delta={cmp.refundRateDelta}
            info="Total refunded amount as a share of gross merchandise value in range."
          />
          {!isLive && (
            <KpiTileV2
              title={intl.formatMessage(analyticsMessages.shippingMargin)}
              value={`${(data.shippingMargin * 100).toFixed(1)}%`}
              icon={<Wallet size={16} />}
              accentColor={chartPalette.amber}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};
